import {
  UV_INSERT_TEXT_LIST_BULLET_DEFAULTS,
  type UVInsertFaceElementTextListConfig,
} from "../../models/uv-inserts"
import { BIRTHSTONE_STAR_CIRCLE_RATIO } from "./birthstone-star"
import { resolveFontSizePx } from "./font-size"

/** The line height both renderers assume when the element declares none. */
const DEFAULT_LINE_HEIGHT = 1.2

/**
 * The resolved geometry and colours of a `TEXT_LIST`'s birthstone star, in face px.
 *
 * The centre circle's colour is per row (the birthstone), so it is not part of this: each renderer
 * reads it from the row.
 */
export type UvInsertTextListStarMetrics = {
  /** Width and height of the star's square box. */
  size: number
  /** Px between the star's right point and the headline's first letter; `0` when the config sets none. */
  gap: number
  /** Height of the headline line: the taller of the text's own line box and the star. */
  lineHeightPx: number
  /** Radius of the centre circle. */
  circleRadius: number
  fillColor: string
  outlineColor: string
  outlineWidth: number
}

export type GetUvInsertTextListStarMetricsOptions = {
  element: UVInsertFaceElementTextListConfig
}

/**
 * The star metrics for a `TEXT_LIST` element, or `null` in glyph mode.
 *
 * One resolver for both renderers, mirroring {@link getUvInsertTextListRows}: every field reads
 * through `UV_INSERT_TEXT_LIST_BULLET_DEFAULTS`, and the gap has no default, so an absent one means
 * flush. `lineHeightPx` resolves the font size with `resolveFontSizePx` (rem-aware, 21 px when
 * absent), the same call the canvas preview makes for its text, so both renderers stack rows alike.
 *
 * @param options - Options object.
 * @param options.element - The parsed `TEXT_LIST` element config.
 * @returns The star metrics, or `null` when the bullet is a glyph.
 */
export function getUvInsertTextListStarMetrics({
  element,
}: GetUvInsertTextListStarMetricsOptions): UvInsertTextListStarMetrics | null {
  const { bullet } = element
  const defaults = UV_INSERT_TEXT_LIST_BULLET_DEFAULTS
  if ((bullet.shape ?? defaults.shape) !== "star") return null

  const size = bullet.size ?? defaults.size
  const textLinePx = resolveFontSizePx(element.fontSize) * (element.lineHeight ?? DEFAULT_LINE_HEIGHT)

  return {
    size,
    gap: bullet.gap ?? 0,
    lineHeightPx: Math.max(textLinePx, size),
    circleRadius: BIRTHSTONE_STAR_CIRCLE_RATIO * (size / 2),
    fillColor: bullet.fillColor ?? defaults.fillColor,
    outlineColor: bullet.outlineColor ?? defaults.outlineColor,
    outlineWidth: bullet.outlineWidth ?? defaults.outlineWidth,
  }
}

/**
 * Extra space above a row's first moment line, in px, so that line's centre sits `momentOffset` px
 * below the headline line's centre. `0` when the element sets no `momentOffset`, which keeps the
 * moment stacked directly under the headline.
 *
 * One calculation for both renderers: the headline line is as tall as the star when there is one,
 * otherwise as tall as the text's own line box, and the moment line is always the text's line box.
 * It can be negative when the offset is smaller than the stacked distance.
 *
 * @param options - Options object.
 * @param options.element - The parsed `TEXT_LIST` element config.
 * @returns The margin above the moment, in px.
 */
export function getUvInsertTextListMomentMargin({
  element,
}: GetUvInsertTextListStarMetricsOptions): number {
  if (element.momentOffset === undefined) return 0

  const textLinePx = resolveFontSizePx(element.fontSize) * (element.lineHeight ?? DEFAULT_LINE_HEIGHT)
  const star = getUvInsertTextListStarMetrics({ element })
  const headlineLinePx = star ? star.lineHeightPx : textLinePx

  return element.momentOffset - (headlineLinePx / 2 + textLinePx / 2)
}
